import { html, type Html, Page, View } from 'rune-ts';
import klass from './SearchResult.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '../../components/cells/Footer/constant';
import { ProductCardListView } from '../../components/cells/ProductCardList/ProductCardListView.ts';
import { Tabs } from '../../components/cells/Tabs/Tabs';
import { updateUrlParams } from '../../../shared/util';
import { CurationListItemView } from '../../components/cells/CurationListItem/CurationListItem';
import { Pagination, PaginationChangeEvent } from '../../components/cells/Pagination/Pagination';
import { CurationListItem } from '../../../features/Curation/type';
import { Dropdown, DropdownChangeEvent } from '../../components/cells/Dropdown/Dropdown';
import { PRODUCT_LIST_SORT } from '../../../features/ProductList/constant';
import {
  PopupStoreItemCard,
  PopupStoreItemCardProps,
} from '../../components/cells/PopupStore/PopupStoreItemCard';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { PlusShopCard } from '../../components/cells/PlusShopCard/PlusShopCard';
import { PlusShopCardType } from '../../../features/PlusShop/type';
import { generateSearchUrl } from '../../../features/Search/const/url';
import { ProductCardSize, ProductCardViewData } from '@services/MarppleShop/features/ProductList/type';
import { font, staticFont } from '@services/MarppleShop/shared/typography';
import { CreatorSearchData } from '@services/MarppleShop/features/Creator/type';
import { CreatorSearchCard } from '@services/MarppleShop/renderApp/components/cells/CreatorSearchCard/CreatorSearchCard.ts';
import { SearchPageController } from '@services/MarppleShop/renderApp/pages/SearchResult/SearchResult.controller.ts';
import { DetachedSearchTitleView } from '@services/MarppleShop/renderApp/pages/SearchResult/DetachedSearchTitleView.ts';

type TSearchResultPageBase = {
  adult_check_needed: boolean;
  keyword: string;
  count: {
    all: number;
    product: number;
    plus_shop: number;
    creator: number;
    popup_store: number;
    curation: number;
  };
  layout_options: BaseLayoutOptions;
  activeTabs: {
    tabId: 'all' | 'product' | 'creator' | 'plus_shop' | 'popup_store' | 'curation';

    /**
     * 해당 tab에서 총 결과의 개수
     */
    totalCount: number;
  }[];
};

type TAllSearchResult = TSearchResultPageBase & {
  current_tab: 'all';
  items: {
    products: ProductCardViewData[];
    creators: CreatorSearchData[];
    plus_shops: PlusShopCardType[];
    curations: CurationListItem[];
    popup_stores: PopupStoreItemCardProps['item'][];
    recommended_products?: ProductCardViewData[];
  };
};

type TProductSearchResult = TSearchResultPageBase & {
  current_tab: 'product';
  items: {
    products: ProductCardViewData[];
    recommended_products?: ProductCardViewData[];
  };
  current_page: number;
  sort: 'price_low_to_high' | 'price_high_to_low' | 'newest' | 'popular' | 'many_reviews';
};

type TPlusShopSearchResult = TSearchResultPageBase & {
  current_tab: 'plus_shop';
  items: {
    plus_shops: PlusShopCardType[];
    recommended_products?: ProductCardViewData[];
  };
  current_page: number;
};

type TCreatorSearchResult = TSearchResultPageBase & {
  current_tab: 'creator';
  items: {
    creators: CreatorSearchData[];
    recommended_products?: ProductCardViewData[];
  };
  current_page: number;
};

type TPopupStoreSearchResult = TSearchResultPageBase & {
  current_tab: 'popup_store';
  items: {
    popup_stores: PopupStoreItemCardProps['item'][];
    recommended_products?: ProductCardViewData[];
  };
  current_page: number;
};

type TCurationSearchResult = TSearchResultPageBase & {
  current_tab: 'curation';
  items: {
    curations: CurationListItem[];
    recommended_products?: ProductCardViewData[];
  };
  current_page: number;
};

export type TSearchResultPageData =
  | TAllSearchResult
  | TProductSearchResult
  | TPlusShopSearchResult
  | TCreatorSearchResult
  | TPopupStoreSearchResult
  | TCurationSearchResult;

/**
 *
 * @figma
 * https://www.figma.com/design/IYgIHeSGeigEZejnJq8xAa/Master?node-id=2351-407513&t=HecPLVgjM6q2iN7T-11
 *
 * @legacy
 * https://github.com/marpple/marpple-web/blob/829617790b2fe423da1c5cfd25ffa8fcdf165c46/services/MarppleShop/renderApp/pages/SearchResult/SearchResult.page.ts
 */
class SearchResultPageBodyView extends View<TSearchResultPageData> {
  private controller = new SearchPageController(this.data.layout_options);

  protected override template() {
    const { keyword, count, current_tab, layout_options, activeTabs } = this.data;
    const { recommended_products } = this.data.items;
    const is_mobile = layout_options.is_mobile;
    const totalCount = count.all;

    const EMPTY_IMAGE_SRC =
      '//s3.marpple.co/files/u_2232571/2024/6/original/a57e2be49b0b2c16047dc905043fa2e5c9fbff141.png';

    if (totalCount <= 0) {
      const searchTitleHtml = html`
        <div class="${font('32_medium')} ${klass.title_container}">
          ${new DetachedSearchTitleView({ totalCount, keyword })}
        </div>
      `;

      const emptyScreenHtml = html`<div class="${klass.search_empty}">
        <img class=${klass.image} src="${EMPTY_IMAGE_SRC}" alt="" />
        <span class="${klass.title} ${font('20_bold')}">${ET('mps2::search::empty_title')}</span>
        <span class="${klass.body} ${font('16_medium')}"
          >${html.preventEscape(ET('mps2::search::empty_body'))}</span
        >
      </div>`;

      if (recommended_products && recommended_products.length > 0) {
        return html`<div class="${klass.search_page}" data-current_tab="all">
          ${searchTitleHtml} ${emptyScreenHtml}
          <div class="${klass.recommend_section}">
            <span class="${font('16_bold', '12_medium')} ${klass.section_title}">
              ${ET('mps2::product::recommend')}
            </span>
            ${new ProductCardListView(
              { products: recommended_products },
              {
                size: ProductCardSize.medium,
                resize_width: is_mobile ? 400 : 600,
                is_lazy: true,
                container_klass: is_mobile
                  ? klass.recommended_products_container
                  : klass.product_cards_container,
                klass: klass.product_card,
              },
            )}
          </div>
        </div>`;
      } else {
        return html`<div class="${klass.search_page}" data-current_tab="all">
          ${searchTitleHtml} ${emptyScreenHtml}
        </div>`;
      }
    } else {
      const tabsExcludingAll = activeTabs.filter((tab) => tab.tabId !== 'all');

      let tabsHtml: View | Html;
      if (tabsExcludingAll.length >= 2) {
        tabsHtml = new Tabs({
          size: 'small',
          current_tab_id: current_tab,
          tabs: activeTabs.map((activeTab) => {
            let href: string;
            let label: string;

            if (activeTab.tabId === 'all') {
              href = generateSearchUrl(keyword);
              label = ET('mps2::search::all');
            } else if (activeTab.tabId === 'product') {
              href = generateSearchUrl(keyword, 'product');
              label = ET('mps2::search::product');
            } else if (activeTab.tabId === 'plus_shop') {
              href = generateSearchUrl(keyword, 'plus_shop');
              label = ET('mps2::search::plus_shop');
            } else if (activeTab.tabId === 'creator') {
              href = generateSearchUrl(keyword, 'creator');
              label = ET('mps2::search::creator');
            } else if (activeTab.tabId === 'popup_store') {
              href = generateSearchUrl(keyword, 'popup_store');
              label = ET('mps2::search::popup_store');
            } else if (activeTab.tabId === 'curation') {
              href = generateSearchUrl(keyword, 'curation');
              label = ET('mps2::search::curation');
            } else {
              throw new Error(`unknown tab id: ${activeTab.tabId}`);
            }

            return {
              href,
              label,
              id: activeTab.tabId,
              data: {
                count: activeTab.totalCount,
              },
            };
          }),
          custom_klass: klass.tab_container,
        });
      } else {
        tabsHtml = html``;
      }

      if (current_tab === 'all') {
        const { adult_check_needed } = this.data;
        const { products, plus_shops, creators, popup_stores, curations } = this.data.items;

        let productSectionHtml;
        if (products.length > 0) {
          const productSectionUrl = generateSearchUrl(keyword, 'product');

          productSectionHtml = html`<div class="${klass.product_section}">
            <div class="${klass.section_title} ${font('24_bold', '16_bold')}" data-count="${count.product}">
              <span>
                ${ET('mps2::search::product')}
                <span class="${klass.count} ${staticFont('unica_24_bold', 'unica_16_bold')}">
                  ${count.product.toLocaleString()}
                </span>
              </span>
              <a href="${productSectionUrl}" class="${klass.see_more} ${font('16_medium', '12_medium')}">
                ${ET('mps2::gnb::more')}
              </a>
            </div>
            ${new ProductCardListView(
              { products },
              {
                resize_width: 600,
                is_lazy: true,
                container_klass: klass.product_cards_container,
                klass: klass.product_card,
                hide_badges: is_mobile,
                hide_tag: is_mobile,
                size: is_mobile ? ProductCardSize.xSmall : ProductCardSize.medium,
              },
            )}
          </div>`;
        } else {
          productSectionHtml = html``;
        }

        let plusShopSectionHtml;
        if (plus_shops.length > 0) {
          const plushShopSectionUrl = generateSearchUrl(keyword, 'plus_shop');

          plusShopSectionHtml = html`<div class="${klass.plus_shop_section}">
            <div class="${klass.section_title} ${font('24_bold', '16_bold')}" data-count="${count.plus_shop}">
              <span>
                ${ET('mps2::search::plus_shop')}
                <span class="${klass.count} ${staticFont('unica_24_bold', 'unica_16_bold')}">
                  ${count.plus_shop.toLocaleString()}
                </span>
              </span>
              <a href="${plushShopSectionUrl}" class="${klass.see_more} ${font('16_medium', '12_medium')}">
                ${ET('mps2::gnb::more')}
              </a>
            </div>

            <div class="${klass.plus_shops_container}">
              ${plus_shops.map((plus_shop) => new PlusShopCard(plus_shop, { adult_check_needed }))}
            </div>
          </div>`;
        } else {
          plusShopSectionHtml = html``;
        }

        let creatorSectionHtml;
        if (creators.length > 0) {
          const creatorSectionUrl = generateSearchUrl(keyword, 'creator');

          creatorSectionHtml = html`<div class="${klass.creator_section}">
            <div class="${klass.section_title} ${font('24_bold', '16_bold')}" data-count="${count.creator}">
              <span>
                ${ET('mps2::search::creator')}
                <span class="${klass.count} ${staticFont('unica_24_bold', 'unica_16_bold')}">
                  ${count.creator.toLocaleString()}
                </span>
              </span>
              <a href="${creatorSectionUrl}" class="${klass.see_more} ${font('16_medium', '12_medium')}">
                ${ET('mps2::gnb::more')}
              </a>
            </div>
            <div class="${klass.creators_container}">
              ${creators.map((creator, index) => {
                return new CreatorSearchCard({
                  creator,
                  is_mobile,
                  is_lazy: is_mobile && index > 5,
                });
              })}
            </div>
          </div>`;
        } else {
          creatorSectionHtml = html``;
        }

        let popupStoreSectionHtml;
        if (popup_stores.length > 0) {
          const popupStoreSectionUrl = generateSearchUrl(keyword, 'popup_store');

          popupStoreSectionHtml = html`<div class="${klass.popup_store_section}">
            <div
              class="${klass.section_title} ${font('24_bold', '16_bold')}"
              data-count="${count.popup_store}"
            >
              <span>
                ${ET('mps2::search::popup_store')}
                <span class="${klass.count} ${staticFont('unica_24_bold', 'unica_16_bold')}">
                  ${count.popup_store.toLocaleString()}
                </span>
              </span>
              <a href="${popupStoreSectionUrl}" class="${klass.see_more} ${font('16_medium', '12_medium')}">
                ${ET('mps2::gnb::more')}
              </a>
            </div>
            <div class="${klass.popup_stores_container}">
              ${popup_stores.map(
                (popup_store) =>
                  new PopupStoreItemCard({
                    item: popup_store,
                    use_in: 'search',
                    is_mobile,
                  }),
              )}
            </div>
          </div>`;
        } else {
          popupStoreSectionHtml = html``;
        }

        let curationSectionHtml;
        if (curations.length > 0) {
          const curationSectionUrl = generateSearchUrl(keyword, 'curation');

          curationSectionHtml = html`<div class="${klass.curation_section}">
            <div class="${klass.section_title} ${font('24_bold', '16_bold')}" data-count="${count.curation}">
              <span>
                ${ET('mps2::search::curation')}
                <span class="${klass.count} ${staticFont('unica_24_bold', 'unica_16_bold')}">
                  ${count.curation.toLocaleString()}
                </span>
              </span>
              <a href="${curationSectionUrl}" class="${klass.see_more} ${font('16_medium', '12_medium')}">
                ${ET('mps2::gnb::more')}
              </a>
            </div>
            <div class="${klass.curations_container}">
              ${curations.map(
                (curation) =>
                  new CurationListItemView(
                    { curation },
                    {
                      klass: klass.curation,
                      is_mobile,
                      thumbnail_type: is_mobile ? 'long' : 'short',
                      without_products: is_mobile,
                      without_tags: true,
                    },
                  ),
              )}
            </div>
          </div>`;
        } else {
          curationSectionHtml = html``;
        }

        return html`<div class="${klass.search_page}" data-current_tab="all">
          <div class="${font('32_medium')} ${klass.title_container}">
            ${new DetachedSearchTitleView({ totalCount, keyword })}
          </div>
          ${tabsHtml} ${productSectionHtml} ${plusShopSectionHtml} ${creatorSectionHtml}
          ${popupStoreSectionHtml} ${curationSectionHtml}
        </div>`;
      } else if (current_tab === 'product') {
        const SORT_OPTIONS = [
          {
            key: PRODUCT_LIST_SORT.popular,
            value: PRODUCT_LIST_SORT.popular,
            name: ET('mps2::product::sort::popular'),
          },
          {
            key: PRODUCT_LIST_SORT.newest,
            value: PRODUCT_LIST_SORT.newest,
            name: ET('mps2::product::sort::newest'),
          },
          {
            key: PRODUCT_LIST_SORT.price_low_to_high,
            value: PRODUCT_LIST_SORT.price_low_to_high,
            name: ET('mps2::product::sort::price_low_to_high'),
          },
          {
            key: PRODUCT_LIST_SORT.price_high_to_low,
            value: PRODUCT_LIST_SORT.price_high_to_low,
            name: ET('mps2::product::sort::price_high_to_low'),
          },
          {
            key: PRODUCT_LIST_SORT.many_reviews,
            value: PRODUCT_LIST_SORT.many_reviews,
            name: ET('mps2::product::sort::many_reviews'),
          },
        ];

        return html`<div class="${klass.search_page}" data-current_tab="product">
          <div class="${font('32_medium')} ${klass.title_container}">
            ${new DetachedSearchTitleView({ totalCount, keyword })}
          </div>
          ${tabsHtml}
          <div class="${klass.product_section}">
            <div class="${klass.section_title} ${font('24_bold', '16_bold')}" data-count="${count.product}">
              <span>
                ${ET('mps2::search::product')}
                <span class="${klass.count} ${staticFont('unica_24_bold', 'unica_16_bold')}">
                  ${count.product.toLocaleString()}
                </span>
              </span>
              ${new Dropdown(
                {
                  options: SORT_OPTIONS,
                  selected_option_key: this.data.sort,
                },
                {
                  horizontal: 'right',
                  without_container: is_mobile,
                  fit_width_to_longest_name: true,
                  text_align: is_mobile ? 'right' : 'left',
                },
              )}
            </div>
            ${new ProductCardListView(
              { products: this.data.items.products },
              {
                resize_width: 600,
                is_lazy: true,
                container_klass: klass.product_cards_container,
                klass: klass.product_card,
                hide_badges: false,
                hide_tag: false,
                size: is_mobile ? ProductCardSize.xSmall : ProductCardSize.medium,
              },
            )}
          </div>
          ${new Pagination(
            {
              page: this.data.current_page,
              size: is_mobile ? 5 : 10,
              total: Pagination.calcTotal({
                total_count: count.product,
                limit_per_page: 100,
              }),
            },
            { klass: klass.pagination },
          )}
        </div>`;
      } else if (current_tab === 'plus_shop') {
        return html`<div class="${klass.search_page}" data-current_tab="plus_shop">
          <div class="${font('32_medium')} ${klass.title_container}">
            ${new DetachedSearchTitleView({ totalCount, keyword })}
          </div>
          ${tabsHtml}
          <div class="${klass.plus_shop_section}">
            <div class="${klass.section_title} ${font('24_bold', '16_bold')}" data-count="${count.plus_shop}">
              <span>
                ${ET('mps2::search::plus_shop')}
                <span class="${klass.count} ${staticFont('unica_24_bold', 'unica_16_bold')}">
                  ${count.plus_shop.toLocaleString()}
                </span>
              </span>
            </div>
            <div class="${klass.plus_shops_container}">
              ${this.data.items.plus_shops.map(
                (plus_shop) =>
                  new PlusShopCard(plus_shop, { adult_check_needed: this.data.adult_check_needed }),
              )}
            </div>
          </div>
          ${new Pagination(
            {
              page: this.data.current_page,
              size: is_mobile ? 5 : 10,
              total: Pagination.calcTotal({
                total_count: count.plus_shop,
                limit_per_page: 100,
              }),
            },
            { klass: klass.pagination },
          )}
        </div>`;
      } else if (current_tab === 'creator') {
        return html`<div class="${klass.search_page}" data-current_tab="creator">
          <div class="${font('32_medium')} ${klass.title_container}">
            ${new DetachedSearchTitleView({ totalCount, keyword })}
          </div>
          ${tabsHtml}
          <div class="${klass.creator_section}">
            <div class="${klass.section_title} ${font('24_bold', '16_bold')}" data-count="${count.creator}">
              <span>
                ${ET('mps2::search::creator')}
                <span class="${klass.count} ${staticFont('unica_24_bold', 'unica_16_bold')}">
                  ${count.creator.toLocaleString()}
                </span>
              </span>
            </div>
            <div class="${klass.creators_container}">
              ${this.data.items.creators.map((creator, index) => {
                return new CreatorSearchCard({
                  creator,
                  is_mobile,
                  is_lazy: is_mobile && index > 5,
                });
              })}
            </div>
          </div>
          ${new Pagination(
            {
              page: this.data.current_page,
              size: is_mobile ? 5 : 10,
              total: Pagination.calcTotal({
                total_count: count.creator,
                limit_per_page: 100,
              }),
            },
            { klass: klass.pagination },
          )}
        </div>`;
      } else if (current_tab === 'popup_store') {
        return html`<div class="${klass.search_page}" data-current_tab="popup_store">
          <div class="${font('32_medium')} ${klass.title_container}">
            ${new DetachedSearchTitleView({ totalCount, keyword })}
          </div>
          ${tabsHtml}
          <div class="${klass.popup_store_section}">
            <div
              class="${klass.section_title} ${font('24_bold', '16_bold')}"
              data-count="${count.popup_store}"
            >
              <span>
                ${ET('mps2::search::popup_store')}
                <span class="${klass.count} ${staticFont('unica_24_bold', 'unica_16_bold')}">
                  ${count.popup_store.toLocaleString()}
                </span>
              </span>
            </div>
            <div class="${klass.popup_stores_container}">
              ${this.data.items.popup_stores.map(
                (popup_store) =>
                  new PopupStoreItemCard({
                    item: popup_store,
                    use_in: 'search',
                    is_mobile,
                  }),
              )}
            </div>
          </div>
          ${new Pagination(
            {
              page: this.data.current_page,
              size: is_mobile ? 5 : 10,
              total: Pagination.calcTotal({
                total_count: count.popup_store,
                limit_per_page: 100,
              }),
            },
            { klass: klass.pagination },
          )}
        </div>`;
      } else if (current_tab === 'curation') {
        return html`<div class="${klass.search_page}" data-current_tab="curation">
          <div class="${font('32_medium')} ${klass.title_container}">
            ${new DetachedSearchTitleView({ totalCount, keyword })}
          </div>
          ${tabsHtml}
          <div class="${klass.curation_section}">
            <div class="${klass.section_title} ${font('24_bold', '16_bold')}" data-count="${count.curation}">
              <span>
                ${ET('mps2::search::curation')}
                <span class="${klass.count} ${staticFont('unica_24_bold', 'unica_16_bold')}">
                  ${count.curation.toLocaleString()}
                </span>
              </span>
            </div>
            <div class="${klass.curations_container}">
              ${this.data.items.curations.map(
                (curation) =>
                  new CurationListItemView(
                    { curation },
                    {
                      klass: klass.curation,
                      is_mobile,
                      thumbnail_type: is_mobile ? 'long' : 'short',
                      without_products: is_mobile,
                      without_tags: false,
                    },
                  ),
              )}
            </div>
          </div>
          ${new Pagination(
            {
              page: this.data.current_page,
              size: is_mobile ? 5 : 10,
              total: Pagination.calcTotal({
                total_count: count.curation,
                limit_per_page: 100,
              }),
            },
            { klass: klass.pagination },
          )}
        </div>`;
      } else {
        throw new TypeError(`current_tab is invalid: ${current_tab}`);
      }
    }
  }

  protected override onMount() {
    let tabQuery: string | undefined;
    if (this.data.current_tab === 'all') {
      tabQuery = undefined;
    } else if (this.data.current_tab === 'product') {
      tabQuery = 'product';
    } else if (this.data.current_tab === 'plus_shop') {
      tabQuery = 'plus_shop';
    } else if (this.data.current_tab === 'creator') {
      tabQuery = 'creator';
    } else if (this.data.current_tab === 'popup_store') {
      tabQuery = 'popup_store';
    } else if (this.data.current_tab === 'curation') {
      tabQuery = 'curation';
    }

    history.replaceState(
      null,
      '',
      updateUrlParams(location.href, {
        tab: tabQuery,
      }),
    );
  }

  protected override onRender() {
    this.delegate(PaginationChangeEvent, Pagination, (e, view) => {
      const { page } = e.detail;

      location.replace(updateUrlParams(location.href, { page }));
      view.disableClick();
    });

    // product tab only
    if (this.data.current_tab === 'product') {
      this.delegate(
        DropdownChangeEvent<keyof typeof PRODUCT_LIST_SORT>,
        Dropdown<keyof typeof PRODUCT_LIST_SORT>,
        async (e) => {
          const sort = e.detail.option?.value;
          if (!sort) {
            throw new TypeError(`sort is required.`);
          }
          location.replace(updateUrlParams(location.href, { sort, page: 1 }));
        },
      );
    }

    this.controller.delegateFanLetterButtonClick(this);
  }
}

export class SearchResultPage extends Page<TSearchResultPageData> {
  protected override template(): Html {
    const { layout_options } = this.data;

    return html`
      <div>
        ${new LayoutView(
          {
            body: new SearchResultPageBodyView(this.data),
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            full_width: layout_options.is_mobile,
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
